import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthorizeGithubResponse } from 'api/github/handlers/authorizeGithubHandler';
import type { ListOrganizationsResponse } from 'api/github/handlers/listOrganizationsHandler';
import type { ListRepositoriesResponse } from 'api/github/handlers/listRepositoriesHandler';
import type { LoadUserResponse } from 'api/github/handlers/loadUserHandler';
import type { OrganizationDocument } from 'common/github/OrganizationDocument';
import type { RepositoryDocument } from 'common/github/RepositoryDocument';
import Api from 'client/lib/Api';
import { useUserState } from 'client/state/UserState';

const loadOAuthURL = (type: 'user' | 'app') =>
  new Promise<string>((resolve, reject) =>
    Api.post<{ type: string }, AuthorizeGithubResponse>('/github/authorize', { type })
      .then(({ status, url }) => {
        if (status !== 'ok') return reject(status);
        return resolve(url);
      })
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

const loadUser = () =>
  new Promise<{ githubUsername: string; githubAvatarURL: string }>((resolve, reject) =>
    Api.post<unknown, LoadUserResponse>('/github/user/load', {})
      .then(({ status, ...user }) => {
        if (status !== 'ok') return reject(status);
        return resolve(user as any);
      })
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

const loadRepositories = () =>
  new Promise<RepositoryDocument[]>((resolve, reject) =>
    Api.post<unknown, ListRepositoriesResponse>('/github/repository/list', {})
      .then(({ status, repositories }) => {
        if (status !== 'ok') return reject(status);
        return resolve(repositories);
      })
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

const loadOrganizations = () =>
  new Promise<OrganizationDocument[]>((resolve, reject) =>
    Api.post<unknown, ListOrganizationsResponse>('/github/user/organizations', {})
      .then(({ status, organizations }) => {
        if (status !== 'ok') return reject(status);
        return resolve(organizations);
      })
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

export interface GitHubAccessOptions {
  scope?: ('user' | 'repositories' | 'organizations')[];
}

export const useGitHubAccess = ({ scope = [] }: GitHubAccessOptions) => {
  const queryClient = useQueryClient();
  const userID = useUserState((state) => state.user?.userID);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isUnauthenticated, setIsUnauthenticated] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const isLoggedIn = !!userID;

  const user = useQuery({
    // Cache query based on userID
    queryKey: ['gitHub.user', userID],
    queryFn: () => loadUser(),
    enabled: isLoggedIn && scope.includes('user'),
    staleTime: 2 * 60 * 1000,

    // If user is authenticating, constantly poll
    retry: isAuthenticating ? true : 2,
  });

  const githubUsername = user.data?.githubUsername || null;

  const repositories = useQuery({
    // Cache query based on github username
    queryKey: ['gitHub.repositories', githubUsername],
    queryFn: () => loadRepositories(),
    enabled: isLoggedIn && scope.includes('repositories') && !!githubUsername,
    staleTime: 5 * 60 * 1000,

    // If user has requested changes to the installation, constantly poll
    refetchInterval: isPolling ? 5 * 1000 : false,
  });

  const organizations = useQuery({
    // Cache query based on github username
    queryKey: ['gitHub.organizations', githubUsername],
    queryFn: () => loadOrganizations(),
    enabled: isLoggedIn && scope.includes('organizations') && !!githubUsername,
    staleTime: 5 * 60 * 1000,

    // If user has requested changes to the installation, constantly poll
    refetchInterval: isPolling ? 5 * 1000 : false,
  });

  const isLoading =
    (scope.includes('user') && user.isLoading) ||
    (scope.includes('repositories') && repositories.isLoading) ||
    (scope.includes('organizations') && organizations.isLoading);

  const error = user.error || repositories.error || organizations.error;
  const isError = !!error;

  const handleAuthenticate = useCallback(async () => {
    // Only wait for authentication if not already authenticated
    if (isUnauthenticated || isError) {
      setIsAuthenticating(true);
    } else {
      // We are most likely adding new repositories, start polling
      setIsPolling(true);
    }

    // TODO: What about requesting new repository access? How should we instantly update repository listing?

    try {
      const url = await loadOAuthURL(isError ? 'user' : 'app');

      // Open GitHub auth into a new tab
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
      setIsAuthenticating(false);

      // FIXME: Show error
    }
  }, [isUnauthenticated, isError]);

  useEffect(() => {
    // User has not linked a GitHub account
    setIsUnauthenticated(error === 'login required');

    // Access has been revoked at some point, user needs to authenticate again
    setIsUnauthenticated(error === 'login required');
  }, [error]);

  const handleReload = useCallback(() => {
    // Invalidate every query with a key that starts with "gitHub"
    queryClient.invalidateQueries({ queryKey: ['gitHub'] });
  }, []);

  let githubNoticeVariant: 'connect' | 'empty' | 'error' | null = null;
  if (!isLoading && scope.includes('organizations') && !(organizations.data || []).length) githubNoticeVariant = 'empty';
  if (isUnauthenticated) githubNoticeVariant = 'connect';
  if (isError) githubNoticeVariant = 'error';

  return {
    user: user.data || null,
    repositories: repositories.data || [],
    organizations: organizations.data || [],
    authenticate: handleAuthenticate,
    reload: handleReload,
    githubNoticeVariant,
    isUnauthenticated,
    isLoading,
    isAuthenticating,
    isError,
  };
};
